import { Box } from '@chakra-ui/react';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { EDITOR_NAVBAR_ID, EDITOR_TOOLBAR_ID, WIDGET_MENU_ID } from 'modules/Editor/Editor.config';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectIsPageNumberToolbarOpen } from 'modules/Editor/store/editorSettingsSelector';
import { setIsPageNumberToolbarOpen } from 'modules/Editor/store/editorSettingsSlice';
import {
  selectAllPageBackgroundData,
  selectPageNumberCustomRangeAsObject,
  selectPageNumberShowOnPages,
  selectPageNumberStart,
  selectPageNumberStyle,
  selectPageOrder,
} from 'modules/Editor/store/infographSelector';
import { MouseEvent, memo, useCallback, useMemo, useRef } from 'react';
import { isLight } from 'utils/color';
import { ReadOnlyRenderType } from 'widgets/Widget.types';

import { EDITABLE_PAGE_NUMBER_ID, PAGE_NUMBER_EXPORT_CLASSNAME } from './PageNumber.config';
import { calculatePageNumberPosition, getPageColor } from './PageNumber.helpers';
import { ColorContrastProps, PageNumberDisplayProps, PageNumberProps } from './PageNumber.types';

export const PageNumber = memo(({ pageId, zIndex, isReadOnly, renderType }: PageNumberProps) => {
  const pageOrder = useAppSelector(selectPageOrder);
  const pageNumberStart = useAppSelector(selectPageNumberStart);
  const customPageRange = useAppSelector(selectPageNumberCustomRangeAsObject);
  const showOnPages = useAppSelector(selectPageNumberShowOnPages);
  const numberStyle = useAppSelector(selectPageNumberStyle);

  const pageNumber = pageOrder.indexOf(pageId) + 1;
  const hasCustomPageRange = showOnPages === 'custom';

  // if the page number is less than the start page number
  if (!hasCustomPageRange && pageNumberStart > pageNumber) return null;
  // if the page number is not in the custom page range
  if (hasCustomPageRange && !customPageRange[pageNumber]) return null;

  return (
    <PageNumberDisplay
      isReadOnly={isReadOnly}
      pageNumber={pageNumber}
      zIndex={zIndex}
      pageId={pageId}
      numberStyle={numberStyle}
      renderType={renderType}
    />
  );
});
PageNumber.displayName = 'PageNumber';

const pageColorContrastTestId = 'page-color-contrast';

// Sets the color of the page number depending on
// 1. The background color of the page
// 2. The color of the widget behind the page number (TODO: implement)
const ColorContrast = ({ pageId, ariaHidden, children }: ColorContrastProps) => {
  // TODO: probably could update this to a debounced selector
  const pageBackground = useAppSelector(selectAllPageBackgroundData(pageId));
  const color = isLight(getPageColor(pageBackground)) ? '#000000' : '#ffffff';

  return (
    <Box data-testid={pageColorContrastTestId} color={color} aria-hidden={ariaHidden}>
      {children}
    </Box>
  );
};

const PageNumberDisplay = memo(
  ({ pageNumber, zIndex, pageId, isReadOnly, numberStyle, renderType }: PageNumberDisplayProps) => {
    // Only the page number on the editable page has this id
    const id = isReadOnly ? undefined : EDITABLE_PAGE_NUMBER_ID;
    const dispatch = useAppDispatch();
    const isPageNumberToolbarOpen = useAppSelector(selectIsPageNumberToolbarOpen);
    const pageNumberRef = useRef<HTMLDivElement>(null);
    const enableAriaHidden = renderType === ReadOnlyRenderType.ExportStructureTreeList;

    const { color, fontSize, fontFamily, fontWeight, fontStyle, textDecoration } = numberStyle;

    const onPageNumberClick = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        dispatch(setIsPageNumberToolbarOpen(true));
      },
      [dispatch],
    );

    // close the toolbar when clicking outside page number (except the page number toolbar)
    useOutsideClick({
      ref: pageNumberRef,
      handler: () => dispatch(setIsPageNumberToolbarOpen(false)),
      ignoreId: [EDITOR_NAVBAR_ID, EDITOR_TOOLBAR_ID, WIDGET_MENU_ID],
    });

    // Calculate the position of the page number and memoize it
    const { top, left } = useMemo(
      () => calculatePageNumberPosition(pageNumber, fontFamily, fontSize),
      [fontFamily, fontSize, pageNumber],
    );

    return (
      <ColorContrast pageId={pageId} ariaHidden={enableAriaHidden}>
        <Box
          as={'figure'}
          cursor={isReadOnly ? 'initial' : 'pointer'}
          ref={pageNumberRef}
          color={color}
          fontSize={fontSize + 'px'}
          fontFamily={fontFamily}
          fontWeight={fontWeight}
          fontStyle={fontStyle}
          textDecoration={textDecoration}
          position='absolute'
          top={top}
          left={left}
          px='0.6rem'
          zIndex={zIndex}
          id={id}
          data-testid={`page-number-${pageId}`}
          className={PAGE_NUMBER_EXPORT_CLASSNAME}
          userSelect='none'
          title='Page Number'
          onClick={onPageNumberClick}
          style={{ boxShadow: isPageNumberToolbarOpen ? 'var(--vg-shadows-outline)' : 'none' }}
        >
          {pageNumber}
        </Box>
      </ColorContrast>
    );
  },
);
PageNumberDisplay.displayName = 'PageNumberDisplay';
